import * as React from "react"
import Step1 from "./Visual/Step1"
import Step2 from "./Visual/Step2"
import Step3 from "./Visual/Step3"
import Step4 from "./Visual/Step4"
import CalculatorStyle from "./CalculatorStyle"
import { FormDataProps, BUSINESS_TYPES } from "./CalculatorTypes"

export default function ClientCalculator() {
    React.useEffect(() => {
        loadResources()
    }, [])

    const totalSteps = 4
    const [step, setStep] = React.useState(1)
    const [formData, setFormData] = React.useState<FormDataProps>({
        type: "",
        visitors: 0,
        spend: 0,
        result: 0,
        resultWBlytz: 0,
        resultDifference: 0,
        firstName: "",
        lastName: "",
        email: "",
        telnumber: "",
        projectName: "",
    })

    function calculation(visitors: number, result: number) {
        const resultWBlytz = Math.round(result * 1.3 + visitors * 0.6 * 4)
        const resultDifference = resultWBlytz - result
        return { resultWBlytz, resultDifference }
    }

    function formatEuro(value?: number) {
        return typeof value === "number" && !isNaN(value)
            ? value.toLocaleString("de-DE", {
                  style: "currency",
                  currency: "EUR",
                  minimumFractionDigits: 0,
              })
            : ""
    }

    function calculateValue(visitors: number) {
        return visitors * 3.8 * 0.6
    }

    const N8N_WEBHOOK =
        "https://n8n.blytzcashless.com/webhook/client-calculator-website"

    async function sendDataToBackend(
        firstName: string,
        lastName: string,
        email: string,
        telnumber: string,
        projectName: string
    ) {
        await fetch(N8N_WEBHOOK, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                firstName,
                lastName,
                email,
                telnumber,
                projectName,
                type: formData.type,
                visitors: formData.visitors,
                spend: formData.spend,
                result: formData.result,
                resultWBlytz: formData.resultWBlytz,
                resultDifference: formData.resultDifference,
                resultFormatted: formatEuro(formData.result),
                resultWBlytzFormatted: formatEuro(formData.resultWBlytz),
                resultDifferenceFormatted: formatEuro(
                    formData.resultDifference
                ),
                value: calculateValue(formData.visitors),
                source: "Client Calculator",
            }),
        })
    }

    const handleStep1Next = (type: string) => {
        setFormData((prev) => ({ ...prev, type }))
        setStep(2)
    }

    const handleStep2Next = ({
        visitors,
        spend,
        result,
    }: {
        visitors: number
        spend: number
        result: number
    }) => {
        const { resultWBlytz, resultDifference } = calculation(visitors, result)
        setFormData((prev) => ({
            ...prev,
            visitors,
            spend,
            result,
            resultWBlytz,
            resultDifference,
        }))
        setStep(3)
    }

    const handleStep3Next = async ({
        firstName,
        lastName,
        email,
        telnumber,
        projectName,
    }: {
        firstName: string
        lastName: string
        email: string
        telnumber: string
        projectName: string
    }) => {
        setFormData((prev) => ({
            ...prev,
            firstName,
            lastName,
            email,
            telnumber,
            projectName,
        }))
        await sendDataToBackend(
            firstName,
            lastName,
            email,
            telnumber,
            projectName
        )
        setStep(4)
    }

    return (
        <main id="client-calculator" className="mt-8 flex justify-center">
            <div className="bg-white pt-4 pb-4 esm:pl-5 esm:pr-5 space-y-4 flex flex-col items-center justify-center w-full h-full max-w-[1200px]">
                <h2 className="xl:text-4xl md:text-3xl text-2xl text-center poppins-font font-bold mt-2 mb-2 p-2 w-full">
                    Calcola il tuo guadagno EXTRA con Blytz Cashless.
                </h2>
                <div className="flex flex-col items-center justify-center mb-2 px-0 sm:px-2 lg:px-4 pt-5 sm:pt-10 w-full">
                    {/* Step-by-Step Progress-Bar */}
                    <StepIndicator
                        step={step}
                        total={totalSteps}
                        onStepClick={(target) => setStep(target)}
                    />
                    {/* Steps */}
                    <div
                        className="flex flex-grow w-full items-center justify-center mt-4 mb-4 flex-col"
                        style={{ minHeight: "400px" }}
                    >
                        {step === 1 && (
                            <Step1
                                formData={formData}
                                options={BUSINESS_TYPES}
                                onNext={handleStep1Next}
                            />
                        )}
                        {step === 2 && (
                            <Step2
                                formData={formData}
                                config={BUSINESS_TYPES.find(
                                    (obj) => obj.value === formData.type
                                )!}
                                onNext={handleStep2Next}
                            />
                        )}
                        {step === 3 && (
                            <Step3
                                formData={formData}
                                onNext={handleStep3Next}
                            />
                        )}
                        {step === 4 && <Step4 formData={formData} />}
                    </div>
                </div>
            </div>
        </main>
    )
}

// Progress Indicator (unverändert)
const StepIndicator = ({
    step,
    total,
    onStepClick,
}: {
    step: number
    total: number
    onStepClick: (n: number) => void
}) => (
    <div className="flex items-center justify-center gap-2 w-full max-w-lg">
        {Array.from({ length: total }, (_, i) => {
            const current = i + 1
            const reached = current <= step

            return (
                <React.Fragment key={current}>
                    <button
                        type="button"
                        onClick={() => reached && onStepClick(current)}
                        disabled={!reached}
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold
                transition-colors focus:outline-none
                ${
                    current === step
                        ? "bg-primary text-primary-foreground"
                        : reached
                          ? "bg-primary text-primary-foreground hover:bg-primary/70"
                          : "bg-muted text-muted-foreground cursor-default"
                }`}
                    >
                        {current}
                    </button>

                    {current < total && (
                        <div
                            className={`flex w-10 sm:w-16 md:w-20 h-[2px] transition-colors
                    ${reached ? "bg-primary/50" : "bg-muted"}`}
                        />
                    )}
                </React.Fragment>
            )
        })}
    </div>
)

// Ressourcen laden (unverändert)
function loadResources() {
    if (!document.getElementById("custom-theme-style")) {
        const style = document.createElement("style")
        style.id = "custom-theme-style"
        style.setAttribute("type", "text/tailwindcss")
        style.textContent = CalculatorStyle()
        document.head.appendChild(style)
    }
    if (!document.querySelector('meta[name="viewport"]')) {
        const meta = document.createElement("meta")
        meta.name = "viewport"
        meta.content = "width=device-width, initial-scale=1.0"
        document.head.appendChild(meta)
    }
    if (!document.getElementById("tailwind-browser")) {
        const script = document.createElement("script")
        script.src = "https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"
        script.id = "tailwind-browser"
        script.async = true
        document.head.appendChild(script)
    }
}
